import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useRef, useMemo } from "react";
import { useBrands } from "@/hooks/useBrands";

// Hardcoded brand logos as fallback - optimized to small sizes (display ~100-200px wide)
import deanteLogo from "@/assets/deante-logo.png?format=webp&quality=85&w=400";
import busterPunchLogo from "@/assets/buster-punch-logo.png?format=webp&quality=85&w=400";
import kArrayLogo from "@/assets/k-array-logo.png?format=webp&quality=85&w=400";
import victoriaAlbertLogo from "@/assets/victoria-albert-logo.png?format=webp&quality=85&w=400";
import nokenLogo from "@/assets/noken-logo.png?format=webp&quality=85&w=400";
import teceLogo from "@/assets/tece-logo.png?format=webp&quality=85&w=400";
import tresLogo from "@/assets/tres-logo.png?format=webp&quality=85&w=400";
import decorWaltherLogo from "@/assets/decor-walther-logo.webp?format=webp&quality=85&w=400";
import excellentLogo from "@/assets/excellent-logo.png?format=webp&quality=85&w=400";

const HARDCODED_BRANDS = [
  { name: "Buster + Punch", logo: busterPunchLogo, slug: "buster-punch", height: "h-12 md:h-16 lg:h-20" },
  { name: "Victoria + Albert", logo: victoriaAlbertLogo, slug: "victoria-albert", height: "h-6 md:h-8 lg:h-10" },
  { name: "Noken", logo: nokenLogo, slug: "noken", height: "h-6 md:h-8 lg:h-10" },
  { name: "TRES", logo: tresLogo, slug: "tres", height: "h-8 md:h-10 lg:h-12" },
  { name: "Deante", logo: deanteLogo, slug: "deante", height: "h-10 md:h-12 lg:h-16" },
  { name: "TECE", logo: teceLogo, slug: "tece", height: "h-8 md:h-10 lg:h-12" },
  { name: "K-Array", logo: kArrayLogo, slug: "k-array", height: "h-8 md:h-10 lg:h-12" },
  { name: "Decor Walther", logo: decorWaltherLogo, slug: "decor-walther", height: "h-8 md:h-10 lg:h-12" },
  { name: "Excellent", logo: excellentLogo, slug: "excellent", height: "h-6 md:h-8 lg:h-10" },
];

export const Brands = () => {
  const { getLocalizedPath, t } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPausedRef = useRef(false);
  const { brands: dbBrands } = useBrands();

  // Merge: use hardcoded as base, add any DB-only brands
  const displayBrands = useMemo(() => {
    const hardcodedSlugs = new Set(HARDCODED_BRANDS.map(b => b.slug));
    const merged = [...HARDCODED_BRANDS];

    // Override hardcoded brands with DB logo if available
    for (let i = 0; i < merged.length; i++) {
      const dbMatch = dbBrands.find(db => db.slug === merged[i].slug);
      if (dbMatch?.logo_url) {
        merged[i] = { ...merged[i], logo: dbMatch.logo_url, height: dbMatch.logo_height || merged[i].height };
      }
    }

    // Add DB-only brands not in hardcoded list
    for (const db of dbBrands) {
      if (!hardcodedSlugs.has(db.slug) && db.logo_url) {
        merged.push({
          name: db.name,
          logo: db.logo_url,
          slug: db.slug,
          height: db.logo_height || "h-8 md:h-10 lg:h-12",
        });
      }
    }

    return merged;
  }, [dbBrands]);

  // Triplicate for seamless infinite scroll
  const scrollBrands = useMemo(() => [...displayBrands, ...displayBrands, ...displayBrands], [displayBrands]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let animationId: number;
    let scrollPos = 0;
    const speed = 0.5;
    let oneThirdWidth = 0;

    const measure = () => {
      oneThirdWidth = container.scrollWidth / 3;
      scrollPos = oneThirdWidth;
      container.scrollLeft = scrollPos;
    };
    requestAnimationFrame(measure);

    const animate = () => {
      if (!isPausedRef.current && oneThirdWidth > 0) {
        scrollPos += speed;
        if (scrollPos >= oneThirdWidth * 2) {
          scrollPos -= oneThirdWidth;
        }
        container.scrollLeft = scrollPos;
      }
      animationId = requestAnimationFrame(animate);
    };

    const startId = requestAnimationFrame(() => {
      animationId = requestAnimationFrame(animate);
    });

    return () => {
      cancelAnimationFrame(startId);
      cancelAnimationFrame(animationId);
    };
  }, [scrollBrands]);

  return (
    <section className="bg-charcoal pt-12 md:pt-16 lg:pt-20 pb-0 border-t border-cream/10">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-cream font-display text-2xl md:text-3xl tracking-wide">
            {t.brands?.title || "Our Brands"}
          </h2>
        </div>
      </div>

      <div
        className="relative overflow-hidden"
        onMouseEnter={() => { isPausedRef.current = true; }}
        onMouseLeave={() => { isPausedRef.current = false; }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-charcoal to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-charcoal to-transparent z-10 pointer-events-none" />

        <div
          ref={scrollRef}
          className="flex items-center gap-16 md:gap-24 lg:gap-32 py-4 overflow-hidden whitespace-nowrap"
          style={{ scrollbarWidth: "none" }}
        >
          {scrollBrands.map((brand, i) => (
            <Link
              key={`${brand.slug}-${i}`}
              to={getLocalizedPath(`/brand/${brand.slug}`)}
              className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity duration-300"
            >
              <img
                src={brand.logo}
                alt={brand.name}
                loading="lazy"
                decoding="async"
                className={`${brand.height} w-auto object-contain brightness-0 invert`}
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
